import {useEffect,useState} from 'react';

type Purchase={id:string;occurredAt:string;kind:'CASH'|'STRIPE_TEST'|'COMPLIMENTARY';
  locationName:string;description:string;subtotalCents:number;
  saleComment:string|null};
type Page={purchases:Purchase[];hasMore:boolean;
  nextCursor:{occurredAt:string;purchaseId:string}|null};
const money=(cents:number)=>new Intl.NumberFormat('en-US',{
  style:'currency',currency:'USD'}).format(cents/100);

/** Verified purchases on the selected single PWA customer profile.
 * Includes genuine paid Stripe TEST orders and completed PWA cash sales.
 * Legacy Helm purchases not yet reconciled are never invented.
 */
export function CustomerPurchasesPanel({customerId,location}:{
  customerId:string|null;location:string;
}){
  const [items,setItems]=useState<Purchase[]>([]);
  const [cursor,setCursor]=useState<Page['nextCursor']>(null);
  const [more,setMore]=useState(false);
  const [status,setStatus]=useState<'loading'|'ready'|'error'>('loading');
  const [loadingMore,setLoadingMore]=useState(false);
  const [error,setError]=useState('');
  useEffect(()=>{
    setItems([]);setCursor(null);setMore(false);setStatus('loading');
    setError('');
    if(!customerId){setStatus('ready');return;}
    const controller=new AbortController();
    void fetch('/api/v1/staff/customers/'+encodeURIComponent(customerId)+
      '/purchases?'+new URLSearchParams({location,limit:'30'}),{
        credentials:'same-origin',cache:'no-store',signal:controller.signal,
      }).then(async r=>{
      const v=await r.json() as {ok:boolean;data?:Page;error?:{code?:string}};
      if(!r.ok||!v.ok||!v.data)throw Error(v.error?.code??'Purchases unavailable');
      if(!controller.signal.aborted){setItems(v.data.purchases);
        setMore(v.data.hasMore);setCursor(v.data.nextCursor);setStatus('ready');}
    }).catch(e=>{if(!controller.signal.aborted){setStatus('error');
      setError(e instanceof Error?e.message:'Purchases unavailable');}});
    return ()=>controller.abort();
  },[customerId,location]);
  const loadMore=async()=>{
    if(!customerId||!cursor||loadingMore)return;
    setLoadingMore(true);setError('');
    try{
      const q=new URLSearchParams({location,limit:'30',
        beforeAt:cursor.occurredAt,beforeId:cursor.purchaseId});
      const r=await fetch('/api/v1/staff/customers/'+encodeURIComponent(customerId)+
        '/purchases?'+q,{credentials:'same-origin',cache:'no-store'});
      const v=await r.json() as {ok:boolean;data?:Page};
      if(!r.ok||!v.ok||!v.data)throw Error('Older purchases unavailable');
      setItems(prev=>[...prev,...v.data!.purchases]);
      setMore(v.data.hasMore);setCursor(v.data.nextCursor);
    }catch(e){setError(e instanceof Error?e.message:'Older purchases unavailable');}
    finally{setLoadingMore(false);}
  };
  return <section aria-label="Customer purchases" className="ecfs-purchases-panel">
    {status==='loading'&&<p role="status">Loading purchases…</p>}
    {status==='error'&&<p role="alert">{error}</p>}
    {status==='ready'&&<>
      <p>Completed PWA purchases. Historical FloatHelm transactions not yet
        reconciled are excluded.</p>
      <div className="ecfs-record-table-scroll"><table className="ecfs-record-table">
        <thead><tr><th>Date</th><th>Purchase</th><th>Location</th>
          <th>Amount</th><th>Tender</th><th>Sale comment</th></tr></thead>
        <tbody>{items.length?items.map(p=><tr key={p.id}>
          <td>{new Intl.DateTimeFormat('en-US',{dateStyle:'medium',
            timeStyle:'short'}).format(new Date(p.occurredAt))}</td>
          <td>{p.description}</td><td>{p.locationName}</td>
          <td>{money(p.subtotalCents)}</td>
          <td>{p.kind==='STRIPE_TEST'?'Stripe TEST':
            p.kind==='COMPLIMENTARY'?'Complimentary':'Cash'}</td>
          <td>{p.saleComment??''}</td>
        </tr>):<tr><td colSpan={6}>No completed PWA purchases recorded.</td></tr>}</tbody>
      </table></div>
      {more&&<button type="button" disabled={loadingMore}
        onClick={()=>void loadMore()}>{loadingMore?'Loading…':'Load older purchases'}</button>}
      {error&&<p role="alert">{error}</p>}
    </>}
  </section>;
}
